import { z } from 'zod';
import { freeText, label, personName } from './text-policy.js';
/**
 * The states a `VisitorWalkInApproval` row STORES. Must stay identical — same members, same
 * order — to the Prisma enum `VisitorWalkInApprovalStatus` (docs/adr/0011; proven by
 * packages/db/src/visitor-walk-in-approval-enums.test.ts). `EXPIRED` is deliberately absent:
 * it is derived from `expiresAt`, exactly as an invite's is.
 */
export const WALK_IN_APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'DENIED'];
/** What a reader sees: the stored status, or `EXPIRED` for a `PENDING` row past `expiresAt`. ADD ONLY. */
export const WALK_IN_APPROVAL_VIEW_STATUSES = ['PENDING', 'APPROVED', 'DENIED', 'EXPIRED'];
export const walkInApprovalViewStatusSchema = z.enum(WALK_IN_APPROVAL_VIEW_STATUSES);
/** The host's two answers. */
export const WALK_IN_APPROVAL_DECISIONS = ['APPROVED', 'DENIED'];
/** How long a request waits for the host before it reads EXPIRED. Product timing, not a plan limit. */
export const WALK_IN_APPROVAL_TTL_SECONDS = 300;
/**
 * `POST /api/device/v1/walk-in-approvals` (S48-lh45) — a kiosk asking whether this walk-in may
 * proceed. A NEW route: no existing device body changes (adr/0009). Field rules copy
 * `createVisitSchema` so the kiosk can send what it already collected. No location input —
 * the location is the device's own (S1).
 */
export const requestWalkInApprovalSchema = z.object({
    hostPersonId: z.string().min(1),
    visitorFirstName: personName({ max: 100 }),
    visitorLastName: personName({ max: 100 }),
    visitorCompany: label({ max: 120 }).nullable().default(null),
    purpose: freeText({ max: 280 }).nullable().default(null),
    /** A kiosk retrying a timed-out ask must not create a second request. */
    idempotencyKey: z.string().uuid(),
});
/** One approval as the kiosk polls it. No visitor PII: the kiosk already has it. */
export const deviceWalkInApprovalDTOSchema = z.object({
    id: z.string().min(1),
    status: walkInApprovalViewStatusSchema,
    expiresAt: z.string().datetime(),
    decidedAt: z.string().datetime().nullable(),
});
/**
 * The answer to the ask. `required: false` (and `approval: null`) = proceed to
 * `POST /api/device/v1/visits` now. `required: true` = show "waiting for your host" and poll
 * `GET /api/device/v1/walk-in-approvals/:id` until the status leaves PENDING.
 * Clients MUST treat an unknown `status` like `DENIED` (see the host-reply rule, adr/0009).
 */
export const deviceWalkInApprovalAskDTOSchema = z.object({
    required: z.boolean(),
    approval: deviceWalkInApprovalDTOSchema.nullable(),
});
//# sourceMappingURL=walk-in-approval.js.map